import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { environment } from '@env/environment';
import { CompanyEmailSlugCheck } from '@interfaces/company/company-email-slug.response';
import { CompanyService } from '@services/http/company.service';
import { SharedModule } from '@shared/shared.module';
import { catchError, debounceTime, distinctUntilChanged, filter, of, switchMap } from 'rxjs';

/** Erro gravado no controle quando o nome não pode ser usado (a tela mostra a mensagem do backend). */
const SLUG_ERROR = 'emailSlug';

/**
 * Campo "E-mail do Data Room" do cadastro da empresa (card 3400): o nome antes de
 * `@cog-mail...`. Vem preenchido com a sugestão tirada do nome fantasia ou da razão social (no
 * cadastro e na edição de empresa ainda sem nome), e acompanha o nome enquanto a pessoa não mexer no
 * campo; nome já salvo nunca é trocado pela sugestão. Confere a disponibilidade na plataforma inteira
 * enquanto a pessoa digita. Vazio = o endereço é o CNPJ.
 */
@Component({
  selector: 'app-company-email-slug-field',
  imports: [SharedModule, ReactiveFormsModule],
  templateUrl: './company-email-slug-field.component.html',
  styleUrl: './company-email-slug-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyEmailSlugFieldComponent implements OnInit {
  /** Formulário da empresa (o campo usa `controlName` dentro dele). */
  readonly form = input.required<FormGroup>();
  /** Nome do controle do slug no formulário. */
  readonly controlName = input.required<string>();
  /** Nome que gera a sugestão (nome fantasia, ou a razão social). */
  readonly name = input<string>('');
  /** Empresa em edição; nula no cadastro novo. */
  readonly companyId = input<string | null>(null);
  protected readonly suffix = `@${environment.cogMailHost}`;
  /** Explicação do campo, no ícone de informação ao lado do rótulo. */
  protected readonly tooltip =
    'Opcional. Documentos enviados a este e-mail entram na _TRIAGEM da empresa. Vem sugerido pelo ' +
    'nome; vazio, usa o CNPJ (que sempre continua recebendo).';
  protected readonly checking = signal(false);
  protected readonly status = signal<CompanyEmailSlugCheck | null>(null);
  /** O valor atual do campo veio da sugestão (não foi digitado nem salvo antes). */
  protected readonly suggested = signal(false);

  private readonly companyService = inject(CompanyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly name$ = toObservable(this.name);

  ngOnInit(): void {
    this.name$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(name => !!name?.trim()),
        // Falha numa chamada não pode encerrar a stream (o campo pararia de sugerir e de conferir).
        switchMap(name =>
          this.companyService
            .suggestEmailSlug(name, this.companyId())
            .pipe(catchError(() => of(null)))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => this.applySuggestion(result));

    this.control.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((value: string | null) => {
          this.trackTyping();
          const slug = (value ?? '').trim();
          if (!slug) {
            return of(null);
          }
          this.checking.set(true);
          return this.companyService
            .checkEmailSlug(slug, this.companyId())
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => this.applyCheck(result));
  }

  protected get isEmpty(): boolean {
    return !(this.control.value ?? '').trim();
  }

  private get control(): AbstractControl {
    return this.form().get(this.controlName())!;
  }

  private applySuggestion(result: CompanyEmailSlugCheck | null): void {
    const slug = result?.available ? result.slug : null;
    // Preenche com a sugestão só se o campo está vazio ou ainda guarda a sugestão anterior: o que a
    // pessoa digitou e o nome já salvo da empresa ficam como estão.
    if (!slug || this.control.dirty || !(this.isEmpty || this.suggested())) {
      return;
    }
    this.control.setValue(slug);
    this.suggested.set(true);
  }

  /** Pessoa digitou no campo: o valor deixa de ser sugestão. */
  private trackTyping(): void {
    if (this.control.dirty) {
      this.suggested.set(false);
    }
  }

  private applyCheck(result: CompanyEmailSlugCheck | null): void {
    this.checking.set(false);
    this.status.set(result);
    const errors = { ...(this.control.errors ?? {}) };
    delete errors[SLUG_ERROR];
    if (result && !result.available) {
      errors[SLUG_ERROR] = result.message;
    }
    this.control.setErrors(Object.keys(errors).length ? errors : null);
  }
}
